import { Capacitor, registerPlugin } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

const DEFAULT_ALBUM = 'ColdWire';
const PhotoSaver = registerPlugin('PhotoSaver', {
  web: () => ({
    saveImage: async () => ({ saved: false, reason: 'unavailable' }),
  }),
});

const successLocations = {
  ios: 'photos',
  android: 'gallery',
  web: 'download',
};

const failureReasons = {
  permission: 'permission-denied',
  write: 'write-failed',
  fetch: 'fetch-failed',
  invalidData: 'invalid-data',
  unavailable: 'unavailable',
};

const normalizeSource = (source) => {
  if (!source) throw new Error('No image source provided');

  if (typeof source === 'string') {
    if (source.startsWith('data:')) {
      const base64 = source.split(',')[1];
      const mimeMatch = source.match(/^data:(.*?);/);
      const mimeType = mimeMatch?.[1] || 'image/jpeg';
      return { kind: 'base64', base64, mimeType };
    }
    return { kind: 'url', url: source };
  }

  if (source instanceof Blob) {
    return { kind: 'blob', blob: source, mimeType: source.type || 'image/jpeg' };
  }

  throw new Error('Unsupported image source type.');
};

const fetchToBlob = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Unable to fetch image data.');
  }
  const blob = await response.blob();
  return { blob, mimeType: blob.type || 'image/jpeg' };
};

const blobToBase64 = async (blob) => {
  const buffer = await blob.arrayBuffer();
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const base64ToBlob = (base64, mimeType) => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i += 1) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
};

const buildFileName = (mimeType, overrideName) => {
  if (overrideName) return overrideName;
  const ext = (mimeType && mimeType.split('/')[1]) || 'jpg';
  return `coldwire_${Date.now()}.${ext}`;
};

const triggerUrlDownload = (url, fileName) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const ensureAndroidPermission = async () => {
  const status = await Filesystem.checkPermissions();
  if (status.publicStorage === 'granted') return true;
  const request = await Filesystem.requestPermissions();
  return request.publicStorage === 'granted';
};

export const formatSaveMessage = (result) => {
  if (result.ok) {
    if (result.location === 'photos') return 'Saved to Photos';
    if (result.location === 'documents') {
      return result.message || 'Image saved to Files app';
    }
    if (result.location === 'gallery') {
      return `Saved to Gallery → ${DEFAULT_ALBUM}/${result.fileName || ''}`;
    }
    if (result.location === 'download') return 'Image saved to your Downloads folder.';
  } else if (result.reason === failureReasons.permission) {
    return 'Permission is required to save images.';
  } else if (result.reason === failureReasons.fetch) {
    return 'Could not fetch image data to save.';
  } else if (result.reason === failureReasons.invalidData) {
    return 'Image data was not valid for saving.';
  }
  return 'Could not save image.';
};

export const saveToGallery = async (imageSource, options = {}) => {
  const { albumName = DEFAULT_ALBUM, fileName: providedName } = options;
  const platform = Capacitor.getPlatform();
  const source = normalizeSource(imageSource);

  console.log('SAVE HANDLER START');
  if (process.env.NODE_ENV !== 'production') {
    console.debug('[BlueWire] saveToGallery', { platform, sourceKind: source.kind });
  }

  try {
    if (platform === 'ios') {
      // iOS: Use Filesystem API instead of custom plugin
      // Save to Documents folder, user can then save to Photos manually
      const { blob, mimeType } = await (async () => {
        if (source.kind === 'blob') {
          return { blob: source.blob, mimeType: source.mimeType || 'image/jpeg' };
        }
        if (source.kind === 'base64') {
          const blob = base64ToBlob(source.base64, source.mimeType || 'image/jpeg');
          return { blob, mimeType: source.mimeType || blob.type || 'image/jpeg' };
        }
        if (source.kind === 'url') {
          return fetchToBlob(source.url);
        }
        throw new Error('Unsupported image source type.');
      })();

      const resolvedName = buildFileName(mimeType, providedName);
      const base64Data = await blobToBase64(blob);

      try {
        const result = await Filesystem.writeFile({
          path: resolvedName,
          data: base64Data,
          directory: Directory.Cache,
        });

        // Open iOS share sheet - user can tap "Save Image" to save to Photos
        await Share.share({
          title: 'Save Image',
          url: result.uri,
          dialogTitle: 'Save to Photos',
        });

        return {
          ok: true,
          location: 'photos',
          fileName: resolvedName,
        };
      } catch (err) {
        console.error('iOS Filesystem save failed:', err);
        return { ok: false, reason: failureReasons.write, message: err?.message };
      }
    }

    const resolveBlobForWrite = async () => {
      if (source.kind === 'blob') {
        return { blob: source.blob, mimeType: source.mimeType || 'image/jpeg' };
      }
      if (source.kind === 'base64') {
        const blob = base64ToBlob(source.base64, source.mimeType || 'image/jpeg');
        return { blob, mimeType: source.mimeType || blob.type || 'image/jpeg' };
      }
      if (source.kind === 'url') {
        return fetchToBlob(source.url);
      }
      throw new Error('Unsupported image source type.');
    };

    let blob;
    let mimeType;
    try {
      const resolved = await resolveBlobForWrite();
      blob = resolved.blob;
      mimeType = resolved.mimeType;
    } catch (err) {
      if (platform === 'web' && source.kind === 'url') {
        const resolvedName = buildFileName('image/jpeg', providedName);
        triggerUrlDownload(source.url, resolvedName);
        return {
          ok: true,
          location: successLocations.web,
          fileName: resolvedName,
          message: 'Opened image download in your browser.',
        };
      }
      throw err;
    }
    const resolvedName = buildFileName(mimeType, providedName);

    if (platform === 'android') {
      const granted = await ensureAndroidPermission();
      if (!granted) return { ok: false, reason: failureReasons.permission };

      const base64 = await blobToBase64(blob);
      await Filesystem.writeFile({
        path: `Pictures/${albumName}/${resolvedName}`,
        data: base64,
        directory: Directory.ExternalStorage,
        recursive: true,
      });

      return { ok: true, location: successLocations.android, fileName: resolvedName };
    }

    // Web fallback: trigger download
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = resolvedName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);

    return { ok: true, location: successLocations.web, fileName: resolvedName };
  } catch (err) {
    console.error('Photo save failed:', err);
    const message = err?.message || '';
    if (message.toLowerCase().includes('permission') || message.toLowerCase().includes('denied')) {
      return { ok: false, reason: failureReasons.permission };
    }
    if (message.toLowerCase().includes('fetch') || message.toLowerCase().includes('download')) {
      return { ok: false, reason: failureReasons.fetch };
    }
    return {
      ok: false,
      reason: failureReasons.write,
      message: message || 'write-failed',
    };
  }
};
